import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  noPadding?: boolean;
  noHeader?: boolean;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title, noPadding = false, noHeader = false }) => {
  const onCloseRef = React.useRef(onClose);

  React.useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Add a dummy state to the history stack to catch the back button
      window.history.pushState({ modalOpen: true }, '');

      const handlePopState = () => {
        // If the back button is pressed, the modal open state is no longer in history
        onCloseRef.current();
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('popstate', handlePopState);
        // If the modal was closed NOT by the back button, remove our fake history state
        if (window.history.state && window.history.state.modalOpen) {
          window.history.back();
        }
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 z-[99999] transition-opacity backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 animate-fadeIn" onClick={onClose}>
      <div 
        className={`bg-surface-1 border border-glass-border rounded-2xl shadow-2xl w-full max-w-md max-h-[92dvh] overflow-y-auto relative animate-scaleIn my-2 sm:my-8 ${noPadding ? 'p-0' : 'p-4 sm:p-6'}`} 
        onClick={e => e.stopPropagation()}
      >
        {!noHeader && (
          <button onClick={onClose} className="absolute top-1 right-1 sm:top-2 sm:right-2 text-text-secondary hover:text-text-primary transition-colors p-3 min-w-[48px] min-h-[48px] flex items-center justify-center z-[100]">
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
        {!noHeader && title && <h2 className="text-xl font-bold text-primary mb-4 sm:mb-6 pr-8">{title}</h2>}
        {children}
      </div>
    </div>,
    document.body
  );
};